import {getWebEventIdentity} from '../utils/webEventIdentity.js';
import {couponApplyPayloads, couponRemovePayloads, purchaseCouponFields} from '../utils/couponWebEvents.mjs';

const TRACKABLE_PRODUCT_MODES = ['index', 'search'];

function normalizeId(value) {
	if (value == null || value === '') return null;
	return String(value);
}

export function resolveCollectConsent(gdprApproved, cookiebot = globalThis.window?.Cookiebot) {
	if (cookiebot?.consent) {
		return {
			analytics: cookiebot.consent.statistics === true,
			ads: cookiebot.consent.marketing === true,
		};
	}

	return {
		analytics: gdprApproved('analytics') === true,
		ads: gdprApproved('marketing') === true,
	};
}

function resolveEventMode(options, route) {
	const searchQuery = options?.search_q ?? route?.query?.search_q ?? null;
	return searchQuery ? 'search' : 'index';
}

function findSearchField(searchFields, fieldCode, selectedItem) {
	if (!Array.isArray(searchFields)) return null;

	return searchFields.find(field => {
		if (field.code === fieldCode) return true;
		if (selectedItem?.attribute_slug && field.filter_url === selectedItem.attribute_slug) return true;
		return false;
	});
}

function mapSelectedItem(fieldCode, item) {
	const isPrice = fieldCode === 'price';

	return {
		code: isPrice ? null : (item.code ?? item.id ?? null),
		title: item.title ?? '',
		slug: isPrice ? (item.code ?? item.slug ?? null) : (item.slug ?? item.filter_url ?? null),
	};
}

export function buildFiltersEventData({options = {}, response, route}) {
	const meta = response?.data?.search_fields_meta_data;
	const searchFields = response?.data?.search_fields;
	const selected = meta?.selected;

	if (!meta || !Array.isArray(selected) || !selected.length) return null;

	const filters = {};

	selected.forEach(item => {
		const fieldCode = item.attribute_field || item.attribute_slug;
		if (!fieldCode) return;

		if (!filters[fieldCode]) {
			const searchField = findSearchField(searchFields, fieldCode, item);

			filters[fieldCode] = {
				attribute: {
					code: fieldCode,
					title: searchField?.label || item.attribute_title || item.title || fieldCode,
					slug: searchField?.filter_url || item.attribute_slug || null,
				},
				attributeitems: [],
			};
		}

		filters[fieldCode].attributeitems.push(mapSelectedItem(fieldCode, item));
	});

	if (!Object.keys(filters).length) return null;

	return {
		mode: resolveEventMode(options, route),
		search_q: options?.search_q ?? route?.query?.search_q ?? null,
		category: {
			id: normalizeId(meta.id),
			code: meta.code ?? null,
			title: meta.title ?? null,
			slug: null,
		},
		filters,
	};
}

function buildFilterStateKey(data) {
	if (!data) return '';

	return JSON.stringify({
		mode: data.mode,
		search_q: data.search_q,
		category_id: data.category?.id,
		filters: data.filters,
	});
}

let lastFilterStateKey = '';
let firstPageView = true;

const GTM_EVENT_MAP = {
	view_item: 'view_product',
	add_to_cart: 'add_to_cart',
	remove_from_cart: 'delete_from_cart',
	purchase: 'purchase',
};

export function mapProductPayload(source) {
	if (!source) return null;

	const product = source.item || source;
	const id = product.id;
	const code = product.code || product.shopping_cart_code || null;
	const title = product.title || '';

	if (id == null && !code) return null;

	return {
		id: id != null ? (Number(id) || id) : null,
		code,
		title,
	};
}

function resolveProductQty(payload = {}) {
	if (payload.quantity != null && payload.quantity !== '') {
		return Number(payload.quantity) || 1;
	}
	if (payload.added != null) return Number(payload.added) || 1;
	if (payload.removed != null) return Number(payload.removed) || 1;

	const items = payload.items;
	if (!items) return 1;
	if (Array.isArray(items)) return Number(items[0]?.quantity) || 1;

	return Number(items.quantity) || 1;
}

function amount(value) {
	if (value == null || value === '') return null;
	const number = Number(value);
	return Number.isFinite(number) ? number : null;
}

function sku(source) {
	const product = source?.item || source;
	return String(product?.code || product?.sku || product?.shopping_cart_code || '').trim();
}

function purchaseItems(items) {
	return items.map(item => ({
		sku: sku(item),
		quantity: Number(item.quantity) || 1,
		...(amount(item.price_custom ?? item.price) != null ? {unit_price: amount(item.price_custom ?? item.price)} : {}),
		...(amount(item.basic_price_custom ?? item.basic_price) != null ? {list_price: amount(item.basic_price_custom ?? item.basic_price)} : {}),
		...(item.seller_code ? {seller: String(item.seller_code)} : {}),
	})).filter(item => item.sku);
}

function listViewPayload(options, response, route, legacyData) {
	const items = response?.data?.items || [];
	const selected = response?.data?.search_fields_meta_data?.selected || [];
	const applied = new Map();
	selected.forEach(item => {
		const attribute = item.attribute_field || item.attribute_slug;
		if (!attribute) return;
		if (!applied.has(attribute)) applied.set(attribute, []);
		applied.get(attribute).push(String(item.code ?? item.id ?? item.title ?? ''));
	});
	const search = resolveEventMode(options, route) === 'search';
	return {
		list_event_id: crypto.randomUUID(),
		listing_kind: search ? 'search' : 'category',
		interaction: selected.length ? 'filter' : (Number(options?.page) > 1 ? 'page' : options?.sort ? 'sort' : 'initial'),
		results_count: Number(response?.data?.meta_data?.items_total) || items.length,
		results_top_skus: items.map(sku).filter(Boolean).slice(0, 60),
		...(legacyData?.category?.id ? {category_id: String(legacyData.category.id)} : {}),
		...(search ? {query: String(options?.search_q || route?.query?.search_q || '')} : {}),
		...(response?.data?.search_event_id ? {search_event_id: response.data.search_event_id} : {}),
		...(applied.size ? {filters_applied: [...applied].map(([attribute, values]) => ({attribute, values}))} : {}),
		filters_offered: (response?.data?.search_fields || []).map(field => field.code).filter(Boolean),
		...(options?.sort ? {sort: String(options.sort)} : {}),
		page_number: Number(options?.page) || 1,
		page_size: Number(options?.limit) || items.length,
	};
}

export function useEventWeb() {
	const appConfig = useAppConfig();
	const runtimeConfig = useRuntimeConfig();
	const endpoints = useEndpoints();
	const {getInfo} = useInfo();
	const {gdprApproved} = useGdpr();
	const {get: getLang} = useLang();
	const loggedIn = useState('loggedIn', () => false);
	const searchContext = useState('webEventSearchContext', () => null);
	const runtimeCollectMode = runtimeConfig.public?.webEventCollectMode;
	const collectMode = appConfig.eventWeb?.collectMode === true || runtimeCollectMode === true || runtimeCollectMode === 'true';
	const runtimeCouponEvents = runtimeConfig.public?.webEventCouponEventsEnabled;
	const couponEventsEnabled = appConfig.eventWeb?.couponEventsEnabled === true || runtimeCouponEvents === true || runtimeCouponEvents === 'true';

	function getBasePayload() {
		const siteId = Number(getInfo('site_id'));
		const userId = loggedIn.value?.user_id ? Number(loggedIn.value.user_id) : null;

		return {
			site_id: Number.isFinite(siteId) ? siteId : null,
			channel: 'web',
			user_id: Number.isFinite(userId) ? userId : null,
		};
	}

	function collectEnvelope(eventName, payload) {
		const consent = resolveCollectConsent(gdprApproved);
		const identity = getWebEventIdentity(consent.analytics);
		const lang = String(getLang() || '').toLowerCase();
		const locale = {si: 'sl_SI', sl: 'sl_SI', hr: 'hr_HR', ba: 'bs_BA', bs: 'bs_BA'}[lang] || null;
		const route = useRoute();
		const productDetail = eventName === 'view_item' || (eventName === 'coupon_copy' && payload.placement === 'detail');
		const pageType = ['coupon_apply', 'coupon_remove'].includes(eventName) ? 'cart' : productDetail ? 'pdp'
			: (route.query?.search_q ? 'search' : eventName === 'begin_checkout' || eventName === 'purchase' ? 'checkout'
				: route.path === '/' ? 'home' : route.path.includes('cart') || route.path.includes('kosar') ? 'cart' : 'category');
		const page = {type: pageType, url: window.location.href};
		if (eventName === 'page_view' && firstPageView && document.referrer) {
			try { page.referrer_host = new URL(document.referrer).host; } catch {}
		}
		if (eventName === 'page_view') firstPageView = false;
		return {
			event_id: crypto.randomUUID(),
			event_name: eventName,
			client_ts: new Date().toISOString(),
			...identity,
			consent,
			locale,
			user_agent: consent.analytics ? navigator.userAgent : null,
			page,
			payload,
		};
	}

	async function trackEvent(event, data = {}, collectEvent = null, collectOnly = false) {
		if (import.meta.server) return null;
		if (appConfig.eventWeb?.enabled === false) return null;
		if (collectEvent === false && collectMode) return null;

		const endpoint = endpoints.get('_post_hapi_misc_event_web');
		if (!endpoint) {
			console.error('[event-web] Missing _post_hapi_misc_event_web endpoint');
			return null;
		}

		const collect = collectEvent === false ? null : collectEnvelope(collectEvent?.event_name || event, collectEvent?.payload || {});
		const body = {
			...getBasePayload(),
			event: collectMode || collectOnly ? collect?.event_name : event,
			data: collectMode || collectOnly ? collect : {...data, ...(collect ? {collect} : {})},
		};

		try {
			return await useApi(endpoint, {
				method: 'POST',
				body,
				keepalive: true,
			}, {
				cache: false,
				appendLang: false,
			});
		} catch (error) {
			if (appConfig.eventWeb?.debug) {
				console.warn('[event-web] Failed to send event', event, error);
			}
			return null;
		}
	}

	function trackCollectOnly(eventName, payload) {
		return trackEvent(eventName, {}, {event_name: eventName, payload}, true);
	}

	function trackCouponEvent(eventName, payload) {
		try {
			if (resolveCollectConsent(gdprApproved).analytics) {
				window.dataLayer = window.dataLayer || [];
				window.dataLayer.push({
					event: eventName,
					ecommerce: null,
					coupon: payload.code,
					origin: null,
					position: null,
					success: null,
					error_reason: null,
					discount_type: null,
					discount_value: null,
					cart_id: null,
					cart_total_before: null,
					cart_total_after: null,
					placement: null,
					sku: null,
					...payload,
				});
			}
		} catch {
			// A GTM failure must not prevent collecting the event in the CMS.
		}
		return trackCollectOnly(eventName, payload);
	}

	async function trackCouponResult(eventName, response, context) {
		if (!couponEventsEnabled || import.meta.server) return;
		try {
			const payloads = eventName === 'coupon_apply' ? couponApplyPayloads(response, context) : couponRemovePayloads(response);
			await Promise.all(payloads.map(payload => trackCouponEvent(eventName, payload)));
		} catch {
			// Tracking must not interrupt coupon actions.
		}
	}

	async function trackCouponCopy(code, {item, placement} = {}) {
		if (!couponEventsEnabled || import.meta.server) return;
		try {
			if (typeof code !== 'string' || !code.trim() || !['card', 'list', 'detail'].includes(placement)) return;
			const itemSku = sku(item);
			return await trackCouponEvent('coupon_copy', {
				code: code.trim(),
				placement,
				...(itemSku ? {sku: itemSku} : {}),
			});
		} catch {
			// Tracking must not interrupt copying or change its success state.
		}
	}

	function trackFiltersFromProductsResponse(options = {}, response) {
		if (import.meta.server) return null;

		const mode = options?.mode || 'index';
		if (!TRACKABLE_PRODUCT_MODES.includes(mode)) return null;

		const route = useRoute();
		const data = buildFiltersEventData({options, response, route});
		if (!response?.data || !Array.isArray(response.data.items)) return null;
		const collect = {event_name: 'list_view', payload: listViewPayload(options, response, route, data)};
		const filterStateKey = buildFilterStateKey(data) + JSON.stringify({
			path: route.path,
			query: collect.payload.query,
			search_event_id: collect.payload.search_event_id,
			page: collect.payload.page_number,
			sort: collect.payload.sort,
			results: collect.payload.results_top_skus,
		});
		if (filterStateKey === lastFilterStateKey) return null;
		lastFilterStateKey = filterStateKey;
		searchContext.value = collect.payload.search_event_id ? {
			search_event_id: collect.payload.search_event_id,
			list_event_id: collect.payload.list_event_id,
			page_number: collect.payload.page_number,
			config_version: response?.data?.config_version || null,
		} : null;

		return data ? trackEvent('filters', data, collect) : trackCollectOnly('list_view', collect.payload);
	}

	function trackFromGtm(gtmEvent, payload = {}) {
		if (import.meta.server) return null;

		const eventName = GTM_EVENT_MAP[gtmEvent];

		if (gtmEvent === 'purchase') {
			const items = Array.isArray(payload.items) ? payload.items : [];
			if (!items.length) return null;
			const orderId = payload.transaction_id || payload.cart?.cart?.order_id;
			const collect = orderId ? {event_name: 'purchase', payload: {
				order_id: String(orderId),
				...(couponEventsEnabled ? purchaseCouponFields(payload.cart) : {}),
				items: purchaseItems(items),
				currency: getLang('currency_code'),
				...(amount(payload.value) != null ? {total: amount(payload.value)} : {}),
			}} : false;
			if (collectMode) return collect ? trackCollectOnly('purchase', collect.payload) : null;

			let collectAdded = false;
			items.forEach(lineItem => {
				const product = mapProductPayload(lineItem);
				if (!product) return;

				trackEvent('purchase', {
					product,
					qty: Number(lineItem.quantity) || 1,
				}, collectAdded ? false : collect);
				collectAdded = true;
			});

			return null;
		}
		if (gtmEvent === 'begin_checkout') {
			const cartId = couponEventsEnabled ? purchaseCouponFields(payload.cart).cart_id : null;
			return trackCollectOnly('begin_checkout', {
				items: purchaseItems(Array.isArray(payload.items) ? payload.items : []),
				...(cartId ? {cart_id: cartId} : {}),
			});
		}
		if (gtmEvent === 'select_item') {
			const itemSku = sku(payload.items);
			if (useRoute().query?.search_q && searchContext.value?.search_event_id && itemSku) {
				searchContext.value = {...searchContext.value, clicked_sku: itemSku};
				return trackCollectOnly('search_click', {
					search_event_id: searchContext.value.search_event_id,
					sku: itemSku,
					list_event_id: searchContext.value.list_event_id,
					page_number: searchContext.value.page_number,
					...(Number.isInteger(payload.index) ? {position: payload.index} : {}),
					...(searchContext.value.config_version ? {config_version: searchContext.value.config_version} : {}),
				});
			}
			return null;
		}
		if (!eventName) return null;

		const product = mapProductPayload(payload.items);
		if (!product) return null;

		const data = {product};

		if (eventName === 'add_to_cart' || eventName === 'delete_from_cart') {
			data.qty = resolveProductQty(payload);
		}

		const item = payload.items?.item || payload.items;
		const eventSku = sku(item);
		if (!eventSku) return null;
		const collectName = eventName === 'view_product' ? 'view_item'
			: eventName === 'delete_from_cart' ? 'remove_from_cart' : eventName;
		const collectPayload = {sku: eventSku};
		if (collectName === 'add_to_cart' || collectName === 'remove_from_cart') collectPayload.quantity = data.qty;
		const price = amount(item?.price_custom ?? item?.price);
		if (price != null) collectPayload.price = price;
		if (collectName === 'view_item') {
			collectPayload.currency = getLang('currency_code');
			const listPrice = amount(item?.basic_price_custom ?? item?.basic_price);
			if (listPrice != null) collectPayload.list_price = listPrice;
			const availability = {1: 'in_stock', 2: 'backorder', 4: 'low', 5: 'preorder', 7: 'out_of_stock', 9: 'out_of_stock'}[item?.availability_info?.status];
			if (availability) collectPayload.availability = availability;
			if (item?.seller_code) collectPayload.seller = String(item.seller_code);
			if (item?.product_condition) collectPayload.condition_state = String(item.product_condition);
			if (searchContext.value?.clicked_sku === eventSku) {
				collectPayload.source = {kind: 'search', search_event_id: searchContext.value.search_event_id,
					list_event_id: searchContext.value.list_event_id};
			}
		}
		if (collectName === 'add_to_cart' && searchContext.value?.clicked_sku === eventSku) {
			collectPayload.search_event_id = searchContext.value.search_event_id;
		}
		return trackEvent(eventName, data, {event_name: collectName, payload: collectPayload});
	}

	return {
		trackEvent,
		trackFiltersFromProductsResponse,
		trackFromGtm,
		trackCollectOnly,
		trackCouponApply: (response, context) => trackCouponResult('coupon_apply', response, context),
		trackCouponRemove: response => trackCouponResult('coupon_remove', response),
		trackCouponCopy,
		buildFiltersEventData,
		mapProductPayload,
	};
}
